import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { CircleCheck, Clock, X } from "lucide-react";
import { SidePanel } from "@speakai/ui/side-panel";
import { Button } from "@speakai/ui/button";
import { SUGGESTION_COPY } from "@speakai/common/src/feedback/copy";
import {
  SUGGESTION_MAX_LENGTH,
  formatNextAllowedTime,
  isVagueSuggestion,
  loadSuggestionDraft,
  pickFieldLabelIndex,
  saveSuggestionDraft,
  splitChatLine,
  suggestionTextIssue,
} from "@speakai/common/src/feedback/suggestion";
import type { SuggestionDraft, SuggestionResult, SuggestionStep } from "@speakai/common/src/feedback/types";
import { useAuth } from "@/hooks/use-auth";
import { useIntercom } from "@/hooks/use-intercom";
import { cn } from "@/lib/utils/cn";
import { useSendSuggestion } from "@/features/improvements/mutations";
import { useSuggestChange } from "@/features/improvements/use-suggest-change";

const browserStorage = () => window.localStorage;

function currentReplayId(): string | undefined {
  try {
    return Sentry.getReplay()?.getReplayId() ?? undefined;
  } catch {
    return undefined;
  }
}

/** The "Suggest a change" panel: a right-side panel on wider screens and a full-screen view on phones. */
export function SuggestChangePanel() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { isOpen, close } = useSuggestChange();
  const { show: showIntercom } = useIntercom();
  const sendSuggestion = useSendSuggestion();
  const { user } = useAuth();
  const accountKey = user?._id;
  const [text, setText] = useState(() => loadSuggestionDraft(browserStorage, accountKey));
  const [step, setStep] = useState<SuggestionStep>({ status: "compose" });
  const [labelIndex, setLabelIndex] = useState(() => pickFieldLabelIndex());
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setLabelIndex(pickFieldLabelIndex());
  }
  const latestText = useRef(text);
  const openCount = useRef(0);
  useEffect(() => {
    latestText.current = text;
  }, [text]);
  const fieldId = useId();
  const hintId = useId();

  const title = t("IMPROVEMENTS.SUGGEST_CHANGE", SUGGESTION_COPY.title);
  const chatLink = t("DASHBOARD.CHAT_WITH_US", SUGGESTION_COPY.chatLink);
  const editing = step.status === "compose" || step.status === "failed";
  const canSend = editing && suggestionTextIssue(text) === null && !sendSuggestion.isPending;
  const sendLabel = step.status === "failed" ? t("IMPROVEMENTS.TRY_AGAIN", SUGGESTION_COPY.tryAgain) : t("IMPROVEMENTS.SEND", SUGGESTION_COPY.send);

  const updateText = (next: string) => {
    setText(next);
    saveSuggestionDraft(browserStorage, accountKey, next);
  };

  const handleClose = () => {
    openCount.current += 1;
    close();
    setStep({ status: "compose" });
  };

  const chatWithUs = () => {
    handleClose();
    showIntercom();
  };

  const submit = async () => {
    if (!canSend) return;
    const sentDuring = openCount.current;
    const draft: SuggestionDraft = {
      text,
      surface: pathname,
      context: {
        appVersion: __APP_VERSION__,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        os: "other",
        replayId: currentReplayId(),
      },
    };
    const result: SuggestionResult = await sendSuggestion
      .mutateAsync(draft)
      .catch((error: unknown) => ({ status: "failed", error: error instanceof Error ? error.message : String(error) }));
    if (result.status === "sent" && latestText.current === draft.text) updateText("");
    if (sentDuring === openCount.current) setStep(result);
  };

  const onFieldKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void submit();
    }
  };

  const [chatBefore, chatAfter] = splitChatLine(t("IMPROVEMENTS.CHAT_LINE", SUGGESTION_COPY.chatLine, { chat: "{{chat}}" }));

  return (
    <SidePanel
      open={isOpen}
      onClose={handleClose}
      side="right"
      title={title}
      panelId="suggest-change"
      backdrop={false}
      dismissOnOutsideClick={false}
      className="sm:top-[calc(var(--topbar-height)+var(--launch-bar-height))]"
      data-testid="suggest-change-panel"
      header={
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3">
          <button
            type="button"
            onClick={handleClose}
            aria-label={t("ACTION.CLOSE")}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:order-last"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
          <h2 className="min-w-0 flex-1 truncate px-1 text-base font-semibold text-foreground">{title}</h2>
          {editing && (
            <Button size="sm" className="sm:hidden" onClick={() => void submit()} disabled={!canSend} isLoading={sendSuggestion.isPending}>
              {sendLabel}
            </Button>
          )}
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
        {editing && (
          <div className="flex flex-1 flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              {chatBefore}
              <button type="button" onClick={chatWithUs} className="font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {chatLink}
              </button>
              {chatAfter}
            </p>
            <label htmlFor={fieldId} className="text-sm font-medium text-foreground">
              {SUGGESTION_COPY.fieldLabels[labelIndex]}
            </label>
            <textarea
              id={fieldId}
              value={text}
              onChange={(event) => updateText(event.target.value)}
              onKeyDown={onFieldKeyDown}
              maxLength={SUGGESTION_MAX_LENGTH}
              aria-describedby={hintId}
              autoFocus
              className="min-h-40 w-full flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:max-h-80"
            />
            <div className="flex items-start justify-between gap-3 text-xs">
              <p id={hintId} className={cn("text-muted-foreground", isVagueSuggestion(text) && "font-medium text-foreground")}>
                {t("IMPROVEMENTS.HINT", SUGGESTION_COPY.hint)}
              </p>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {text.length} / {SUGGESTION_MAX_LENGTH}
              </span>
            </div>
            {step.status === "failed" && (
              <p role="alert" className="text-sm text-destructive">
                {t("IMPROVEMENTS.FAILED", SUGGESTION_COPY.failed)}
              </p>
            )}
            <div className="hidden items-center justify-end gap-2 sm:flex">
              <Button variant="outline" onClick={handleClose}>
                {t("ACTION.CANCEL")}
              </Button>
              <Button onClick={() => void submit()} disabled={!canSend} isLoading={sendSuggestion.isPending}>
                {sendLabel}
              </Button>
            </div>
          </div>
        )}

        {step.status === "sent" && (
          <Outcome icon={<CircleCheck className="h-8 w-8 text-primary" aria-hidden="true" />} title={t("IMPROVEMENTS.SENT_TITLE", SUGGESTION_COPY.sentTitle)} body={t("IMPROVEMENTS.SENT_BODY", SUGGESTION_COPY.sentBody)}>
            <Button variant="outline" onClick={() => setStep({ status: "compose" })}>
              {t("IMPROVEMENTS.SUGGEST_ANOTHER", SUGGESTION_COPY.suggestAnother)}
            </Button>
            <Button onClick={handleClose}>{t("ACTION.DONE")}</Button>
          </Outcome>
        )}

        {step.status === "rate_limited" && (
          <Outcome
            icon={<Clock className="h-8 w-8 text-muted-foreground" aria-hidden="true" />}
            title={t("IMPROVEMENTS.LIMIT_TITLE", SUGGESTION_COPY.limitTitle)}
            body={t("IMPROVEMENTS.LIMIT_BODY", SUGGESTION_COPY.limitBody, { time: formatNextAllowedTime(step.nextAllowedAt, i18n.language) })}
          >
            <Button variant="outline" onClick={chatWithUs}>
              {chatLink}
            </Button>
            <Button onClick={handleClose}>{t("IMPROVEMENTS.OK", SUGGESTION_COPY.ok)}</Button>
          </Outcome>
        )}
      </div>
    </SidePanel>
  );
}

function Outcome({ icon, title, body, children }: { icon: ReactNode; title: string; body: string; children: ReactNode }) {
  return (
    <div role="status" className="flex flex-1 flex-col items-center justify-center gap-3 px-2 text-center">
      {icon}
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <p className="max-w-sm text-sm text-muted-foreground">{body}</p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">{children}</div>
    </div>
  );
}
